import React, { useRef, useState, useEffect } from "react";

import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import "react-international-phone/style.css";

import { Button } from "primereact/button";

import { appointmentsAdd, appointmentsUpdate } from "../../service/appointmentsService";
import AutoCompleteApi from "../../components/AutoCompleteApi";

import { Grid } from "@mui/material";
import { TabPanel, TabView } from "primereact/tabview";
import { QuillEditor } from "../../components/QuillEditor";

import { Input, MultiSelect, Select, Switch } from "../../components/Shared/Forms";
import useApiService from "../../hooks/useApiService";

export default function AppointmentsAdd(modalProps: any) {
  const record = modalProps.record;

  const [name, setName] = useState(record?.name || "");
  const [access, setAccess] = useState(record?.access_level || "");
  const [parentAppointment, setParentAppointment] = useState(record?.parent_appointment || "");
  const [industry, setIndustry] = useState(record?.industry || []);
  const [required, setRequired] = useState(record?.required || false);
  const [form, setForm] = useState(record?.form || "");
  const [legalReference, setLegalReference] = useState(record?.legal_reference || "");
  const [nameError, setNameError] = useState(false);
  const [accessError, setAccessError] = useState(false);

  // const quillEditorRef = useRef<{ validate: () => QuillEditorValidateReturn }>(null);
  const quillEditorRef = useRef();
  const [placeholderErrors, setPlaceholderErrors] = useState<string[]>([]);

  const { loading: loadingAccessLevel, data: accessLevelData } = useApiService<any[]>({
    method: "GET",
    url: "access-level",
    apiName: "core",
  });

  const { loading: loadingAppointments, data: appointmentData } = useApiService<any[]>({
    method: "GET",
    url: "appointments",
    apiName: "core",
  });

  const { loading: loadingIndustries, data: industries } = useApiService<any[]>({
    method: "GET",
    url: "industry",
    apiName: "core",
  });

  const validateForm = async () => {
    //@ts-ignore
    const formValid = quillEditorRef.current?.validate();
    if (formValid?.hasPlaceholderErrors?.length > 0) {
      setPlaceholderErrors(formValid.hasPlaceholderErrors);
    } else {
      setPlaceholderErrors([]);
    }
  };

  useEffect(() => {
    const debounceValidation = setTimeout(() => {
      validateForm();
    }, 1000);

    return () => clearTimeout(debounceValidation); // Cleanup on value change
  }, [form]);

  async function addOrUpdate() {
    const updateObject: any = {
      name: name,
      access_level: access,
      legal_reference: legalReference,
      required,
      form,
      industry,
    };
    if (parentAppointment && parentAppointment != "") {
      updateObject.parent_appointment = parentAppointment;
    }
    setNameError(!name || name === "");
    setAccessError(!access || access === "");
    if (!name || name === "" || !access || access === "") {
      modalProps.toast.current.show({
        severity: "warn",
        summary: "Please enter all required fields",
        life: 3000,
      });
      return;
    }

    //TODO: Comment back in to prevent saving if form is not valid
    //@ts-ignore
    const formValid = quillEditorRef.current?.validate();
    // console.log("formValid", formValid);
    // if(!formValid) {
    //   modalProps.toast.current.show({
    //     severity: "warn",
    //     summary: "Complete the Form",
    //     life: 3000,
    //   });
    //   return;
    // }
    // if(formValid.hasPlaceholderErrors.length > 0) {
    //   setPlaceholderErrors(formValid.hasPlaceholderErrors)
    //   modalProps.toast.current.show({
    //     severity: "warn",
    //     summary: "Fix form placeholder errors",
    //     life: 3000,
    //   });
    //   return;
    // }
    if (!formValid?.hasOneSignature) {
      modalProps.toast.current.show({
        severity: "warn",
        summary: "At least one signature placeholder is required",
        life: 3000,
      });
      return;
    }

    modalProps.setGlobalLoader(true);
    if (modalProps.id) {
      appointmentsUpdate(updateObject, record.id).then((r: any) => {
        modalProps.setVisible(false);
        modalProps.setGlobalLoader(false);
        modalProps.setReload(!modalProps.reload);
        modalProps.toast.current.show({
          severity: "success",
          summary: "Appointment Updated Successfully",
          life: 3000,
        });
      });
    } else {
      appointmentsAdd(updateObject).then((r: any) => {
        modalProps.setVisible(false);
        modalProps.setGlobalLoader(false);
        modalProps.setReload(!modalProps.reload);
        modalProps.toast.current.show({
          severity: "success",
          summary: "Appointment Added Successfully",
          life: 3000,
        });
      });
    }
  }

  return (
    <React.Fragment>
      <TabView>
        <TabPanel header="Details">
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Input
                label={"Appointment Name"}
                required={true}
                placeholder={"Eg. Security Officer"}
                onChange={e => setName(e.target.value)}
                value={name}
              />
            </Grid>
            <Grid item xs={6} style={{ marginTop: "2rem" }}>
              <Switch
                checked={required}
                label={"Required?"}
                onChange={(e: any) => setRequired(e.value)}
                subLabel={"Should this label be required by sites?"}
              />
            </Grid>
            <Grid item xs={12}>
              <AutoCompleteApi
                apiPath="legal-reference"
                optionLabel="legal_reference"
                optionValue="legal_reference"
                label="Legal Reference"
                selectedData={legalReference}
                setSelectedData={setLegalReference}
              />
            </Grid>
            <Grid item xs={6}>
              <Select
                options={
                  accessLevelData
                    ?.filter((item: any) => item.name !== "All")
                    .map((item: any) => ({
                      value: item.id,
                      label: item.name,
                    })) as { value: any; label: any }[]
                }
                label={"Access Level"}
                required={true}
                loading={loadingAccessLevel}
                onChange={e => {
                  setAccess(e.value);
                }}
                value={access}
                filter={true}
              />
            </Grid>
            <Grid item xs={6}>
              <Select
                options={
                  appointmentData
                    ?.filter((item: any) => item.name !== "All")
                    .map((item: any) => ({
                      value: item.id,
                      label: item.name,
                    })) as { value: any; label: any }[]
                }
                label={"Reports to"}
                loading={loadingAppointments}
                onChange={e => {
                  setParentAppointment(e.value);
                }}
                value={parentAppointment}
                filter={true}
              />
            </Grid>
            <Grid item xs={12}>
              <MultiSelect
                options={
                  industries?.map((industry: any) => ({
                    label: industry.name,
                    value: industry.id,
                  })) ?? []
                }
                label={"Industry"}
                onChange={(e: any) => {
                  setIndustry((_: any[]) => [...e.value]);
                }}
                value={industry}
                onSelectAll={(val: any) => {
                  setIndustry(val);
                }}
                loading={loadingIndustries}
                showSelectAll={false}
              />
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel header="Form">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <QuillEditor
                label="Body"
                content={form}
                setContent={setForm}
                toast={modalProps.toast}
                ref={quillEditorRef}
              />
              <br />
              {placeholderErrors.length > 0 && (
                <div
                  style={{
                    color: "black",
                    background: "rgba(255,191,0,0.22)",
                    padding: "20px 20px 10px 20px",
                    marginBottom: 10,
                    borderRadius: 5,
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>
                    {" "}
                    ⚠️ The following placeholders do not exist:
                  </span>{" "}
                  <ul>
                    {placeholderErrors.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Grid>
          </Grid>
        </TabPanel>
      </TabView>

      <CardOverflow
        style={{ width: "100%" }}
        sx={{ borderTop: "1px solid", borderColor: "divider" }}
      >
        <CardActions style={{ marginTop: 15 }} sx={{ alignSelf: "flex-end", pt: 2 }}>
          <Button
            type="button"
            icon="pi pi-save"
            label="Save"
            className="p-button-outlined"
            size="small"
            style={{
              position: "absolute",
              fontSize: "small",
              right: 0,
              color: "#2dd36f",
            }}
            onClick={() => addOrUpdate()}
          />
        </CardActions>
      </CardOverflow>
    </React.Fragment>
  );
}
